/**
 * R1 Scoring API client — flow `dual_claimant_packets`.
 *
 * Mirror endpoint /backend-api/r1-scoring (H.1). KHÔNG có judge-side endpoint.
 */
import { apiSlice } from './apiSlice'

export type R1Side = 'scenario_a' | 'scenario_b'
export type R1Part = 'memorial' | 'evidence'
export type R1ScoreSource = 'ai' | 'admin'
export type R1ScorecardStatus = 'draft' | 'submitted' | 'locked'

export interface R1Scorecard {
  id: string
  case_id: string
  round_id: string | null
  team_id: string
  r1_side: R1Side
  part: R1Part
  score_source: R1ScoreSource
  reviewer_id: string | null
  rubric_id: string | null
  rubric_scores: Record<string, number>
  total_score: number
  max_score: number
  status: R1ScorecardStatus
  is_effective: boolean
  supersedes_score_id: string | null
  ai_error: string | null
  ai_attempt_count: number | null
  created_at: string | null
  updated_at: string | null
  locked_at: string | null
}

export interface R1ScorecardUpdate {
  rubric_id?: string | null
  rubric_scores?: Record<string, number>
  total_score?: number
  status?: R1ScorecardStatus
  ai_error?: string | null
  is_effective?: boolean
}

export interface R1TeamScoreSummary {
  case_id: string
  team_id: string
  team_name: string | null
  scenario_a_memorial: number
  scenario_a_evidence: number
  scenario_b_memorial: number
  scenario_b_evidence: number
  r1_total: number
  video_tiebreak_total: number | null
  statuses: Record<string, 'missing' | 'ai' | 'admin' | 'locked'>
  parts_scored: number
  ai_status: 'pending' | 'partial' | 'done' | 'failed'
  admin_review_status: 'pending' | 'partial' | 'done'
  locked: boolean
}

export interface R1AiRunBody {
  case_id: string
  team_id?: string
  only_failed?: boolean
}

export interface R1ShortlistBody {
  case_id: string
  top_n: number
}

export interface R1PublishTop4Body {
  case_id: string
  top_n: number
}

export const r1ScoringApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listR1Scorecards: builder.query<R1Scorecard[], { case_id: string; team_id?: string }>({
      query: ({ case_id, team_id }) => ({
        url: '/r1-scoring/list',
        params: team_id ? { case_id, team_id } : { case_id },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((sc) => ({ type: 'Score' as const, id: sc.id })),
              { type: 'Score' as const, id: 'R1_LIST' },
            ]
          : [{ type: 'Score' as const, id: 'R1_LIST' }],
    }),
    getR1TeamSummary: builder.query<R1TeamScoreSummary, { case_id: string; team_id: string }>({
      query: ({ case_id, team_id }) => ({
        url: '/r1-scoring/team-summary',
        params: { case_id, team_id },
      }),
      providesTags: (_r, _e, arg) => [
        { type: 'Score' as const, id: `R1_SUM_${arg.case_id}_${arg.team_id}` },
      ],
    }),
    getR1Ranking: builder.query<R1TeamScoreSummary[], { case_id: string }>({
      query: ({ case_id }) => ({
        url: '/r1-scoring/ranking',
        params: { case_id },
      }),
      providesTags: (_r, _e, arg) => [{ type: 'Score' as const, id: `R1_RANK_${arg.case_id}` }],
    }),
    updateR1Scorecard: builder.mutation<R1Scorecard, { scorecard_id: string; body: R1ScorecardUpdate }>({
      query: ({ scorecard_id, body }) => ({
        url: `/r1-scoring/${scorecard_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'Score', id: 'R1_LIST' }, 'Score'],
    }),
    lockR1Scorecard: builder.mutation<R1Scorecard, string>({
      query: (scorecard_id) => ({
        url: `/r1-scoring/${scorecard_id}/lock`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Score', id: 'R1_LIST' }, 'Score'],
    }),
    lockR1Team: builder.mutation<{ locked_count: number }, { case_id: string; team_id: string }>({
      query: ({ case_id, team_id }) => ({
        url: '/r1-scoring/lock-team',
        method: 'POST',
        params: { case_id, team_id },
      }),
      invalidatesTags: [{ type: 'Score', id: 'R1_LIST' }, 'Score'],
    }),
    runR1AiScoring: builder.mutation<{ case_id: string; results: Record<string, Record<string, string>> }, R1AiRunBody>({
      query: (body) => ({
        url: '/r1-scoring/ai/run',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Score', id: 'R1_LIST' }, 'Score'],
    }),
    generateR1Shortlist: builder.mutation<R1TeamScoreSummary[], R1ShortlistBody>({
      query: (body) => ({
        url: '/r1-scoring/shortlist',
        method: 'POST',
        body,
      }),
    }),
    publishR1Top4: builder.mutation<{ case_id: string; top_n: number; advanced_team_ids: string[]; ranking: R1TeamScoreSummary[] }, R1PublishTop4Body>({
      query: (body) => ({
        url: '/r1-scoring/publish-top',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team', 'Score'],
    }),
  }),
})

export const {
  useListR1ScorecardsQuery,
  useGetR1TeamSummaryQuery,
  useGetR1RankingQuery,
  useUpdateR1ScorecardMutation,
  useLockR1ScorecardMutation,
  useLockR1TeamMutation,
  useRunR1AiScoringMutation,
  useGenerateR1ShortlistMutation,
  usePublishR1Top4Mutation,
} = r1ScoringApi
